import { CallRoute } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useCallRouteV1QueryUpdaters } from '../query-updaters';
import { UpdatePhoneNumbersIO } from '../types';
import { UseCallRouteV1MutationEndpointArgs, useCallRouteV1Mutation } from './use-call-route-v1-mutation';

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `UpdatePhoneNumbers` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useUpdatePhoneNumbersMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends UpdatePhoneNumbersIO['input'] = UpdatePhoneNumbersIO['input']
>({
  options,
  // optimisticUpdate = false,
  ...args
}: UseCallRouteV1MutationEndpointArgs<'UpdatePhoneNumbers', E, C | undefined, OtherOptions, RequestOverride> = {}) => {
  const { updateQuery, invalidateQueries } = useCallRouteV1QueryUpdaters();

  return useCallRouteV1Mutation<'UpdatePhoneNumbers', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'UpdatePhoneNumbers',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        // Implement optimistic updates here

        return {
          // Add custom context type here (if exists in `MutationContext`)
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => {
        // Implement query updates or invalidation for affected queries here

        // When successfully updating a call route, update the new call route (or device extension route) in the list
        // of call routes to reflect the updated phone numbers
        updateQuery({
          endpointName: 'List',
          queryFilters: {},
          updater: (data) => ({
            // Update the call routes and device extension routes in the List query response
            callRoutes: updateRoutes(
              data.callRoutes ?? [],
              response.callRouteId,
              response.phoneNumberIds,
              request.phoneNumberIds
            ),
            deviceExtensionRoutes: updateRoutes(
              data.deviceExtensionRoutes ?? [],
              response.callRouteId,
              response.phoneNumberIds,
              request.phoneNumberIds
            ),
          }),
        });

        // When successfully updating a call route, update the Read query cached call route
        // to reflect the new updated phone numbers ids.
        updateQuery({
          endpointName: 'Read',
          queryFilters: {},
          updater: (data) => ({
            ...data,
            callRoute: {
              ...data.callRoute,
              phoneNumberIds: response.phoneNumberIds,
            },
          }),
        });

        invalidateQueries({
          endpointName: 'ListUnallocatedPhoneNumbers',
        });

        invalidateQueries({
          endpointName: 'Usage',
        });

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        // Revert optimistic update here (if implemented)

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};

// Helper function to update the call routes and device extension routes with the new phone numbers
// that were added to the call route from the request. It also removes any phone numbers
// that were removed from the call route in case they were added to another call route
// from the request.
const updateRoutes = (
  routes: CallRoute[],
  routeId: string,
  respPhoneNumberIds: string[],
  reqPhoneNumberIds: string[]
) => {
  return routes.map((route) =>
    route.callRouteId === routeId
      ? // If the call route is the one being updated, return it with the new phone numbers
        // that were added to the call route from the request
        { ...route, phoneNumberIds: respPhoneNumberIds }
      : // If the call route is not the one being updated, return it with any phone
        // numbers that need to be removed in case they were added to the call route from the request
        {
          ...route,
          phoneNumberIds: route.phoneNumberIds.filter((phoneNumberId) => !reqPhoneNumberIds.includes(phoneNumberId)),
        }
  );
};
